import type { PatternDef } from './dsl';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';

const DEG = Math.PI / 180;
const DEFAULT_LIFE = 6;
/** Elongated bullets that should always face their travel direction. */
const DIRECTIONAL = new Set(['bullet/enemy/bolt']);
/** Charge flare sits this far out along the aim (px) — the body's leading edge */
const MUZZLE_OFFSET = 28;
/** Ground-crack reach for `cue: 'line'` volleys (px) */
const CRACK_LENGTH = 640;
/** Wall hole walks this many slots per volley (coprime-ish → it roams) */
const HOLE_STRIDE = 7;

/**
 * Runtime state of a single pattern. The boss keeps one runner per pattern
 * in each phase; runners are recreated on phase change.
 */
export class PatternRunner {
  private timer = 0;
  private spiralAngle = 0;
  private radialAngle = 0;
  private burstLeft = 0;
  private burstTimer = 0;
  /** Countdown between telegraph display and the first volley */
  private pendingVolley = -1;
  /** Walls fired so far — walks the escape hole */
  private wallIndex = 0;

  constructor(private readonly def: PatternDef) {
    this.radialAngle = (def.type === 'radial' ? (def.phaseDeg ?? 0) : 0) * DEG;
  }

  /** True during an aimed volley's wind-up — bosses read it as a body cue. */
  get warning(): boolean {
    return this.pendingVolley >= 0;
  }

  /**
   * @param ox,oy fire origin (boss/turret)
   * @param tx,ty target (player) — used by aimed patterns
   */
  update(
    dt: number,
    ox: number,
    oy: number,
    tx: number,
    ty: number,
    bullets: BulletSystem,
    telegraphs?: TelegraphSink,
  ): void {
    const d = this.def;
    this.timer -= dt;

    switch (d.type) {
      case 'radial': {
        if (this.timer <= 0) {
          this.timer += d.interval;
          for (let i = 0; i < d.count; i++) {
            const a = this.radialAngle + (Math.PI * 2 * i) / d.count;
            this.fire(bullets, ox, oy, Math.cos(a), Math.sin(a), i);
          }
          this.radialAngle += (d.spinDeg ?? 0) * DEG;
        }
        break;
      }

      case 'spiral': {
        this.spiralAngle += d.angularSpeed * DEG * dt;
        if (this.timer <= 0) {
          this.timer += d.interval;
          for (let i = 0; i < d.arms; i++) {
            const a = this.spiralAngle + (Math.PI * 2 * i) / d.arms;
            this.fire(bullets, ox, oy, Math.cos(a), Math.sin(a), i);
          }
        }
        break;
      }

      case 'aimed': {
        // Telegraph delay before the first volley of a burst
        if (this.pendingVolley >= 0) {
          this.pendingVolley -= dt;
          if (this.pendingVolley < 0) {
            this.startBurst(bullets, ox, oy, tx, ty);
          }
          break;
        }
        if (this.burstLeft > 0) {
          this.burstTimer -= dt;
          if (this.burstTimer <= 0) {
            this.burstLeft--;
            this.burstTimer = d.burstGap ?? 0.2;
            this.fireFan(bullets, ox, oy, tx, ty);
          }
        } else if (this.timer <= 0) {
          this.timer += d.interval;
          if (d.telegraph && telegraphs) {
            this.windUp(telegraphs, ox, oy, tx, ty, d.telegraph, d.cue);
          } else {
            this.startBurst(bullets, ox, oy, tx, ty);
          }
        }
        break;
      }

      case 'wall': {
        if (this.pendingVolley >= 0) {
          this.pendingVolley -= dt;
          if (this.pendingVolley < 0) this.fireWall(bullets, ox, oy, tx, ty);
        } else if (this.timer <= 0) {
          this.timer += d.interval;
          if (d.telegraph && telegraphs) {
            this.windUp(telegraphs, ox, oy, tx, ty, d.telegraph, 'charge');
          } else {
            this.fireWall(bullets, ox, oy, tx, ty);
          }
        }
        break;
      }
    }
  }

  /** Telegraph, then hold the volley for `time` (docs/01 telegraph rule). */
  private windUp(
    telegraphs: TelegraphSink, ox: number, oy: number, tx: number, ty: number,
    time: number, cue: 'charge' | 'line' | undefined,
  ): void {
    const dist = Math.hypot(tx - ox, ty - oy) || 1;
    const nx = (tx - ox) / dist;
    const ny = (ty - oy) / dist;
    if (cue === 'line') {
      telegraphs.crack(ox, oy, nx, ny, CRACK_LENGTH, time);
    } else {
      // In-fiction muzzle charge (docs/02): light gathers on the body's
      // leading edge toward the aim — never a screen-long red line.
      telegraphs.charge(ox + nx * MUZZLE_OFFSET, oy + ny * MUZZLE_OFFSET, nx, ny, time);
    }
    this.pendingVolley = time;
  }

  /** A line abreast with a roaming escape hole (WallPattern). */
  private fireWall(bullets: BulletSystem, ox: number, oy: number, tx: number, ty: number): void {
    const d = this.def;
    if (d.type !== 'wall') return;
    const heading = d.headingDeg !== undefined ? d.headingDeg * DEG : Math.atan2(ty - oy, tx - ox);
    const dx = Math.cos(heading);
    const dy = Math.sin(heading);
    const hole = Math.min(d.hole ?? 0, d.count - 1);
    const holeAt = hole > 0 ? (this.wallIndex * HOLE_STRIDE) % (d.count - hole + 1) : -1;
    this.wallIndex++;
    const half = (d.count - 1) / 2;
    for (let i = 0; i < d.count; i++) {
      if (i >= holeAt && i < holeAt + hole) continue;
      const off = (i - half) * d.spacing;
      this.fire(bullets, ox - dy * off, oy + dx * off, dx, dy, i);
    }
  }

  private startBurst(bullets: BulletSystem, ox: number, oy: number, tx: number, ty: number): void {
    const d = this.def;
    if (d.type !== 'aimed') return;
    this.burstLeft = (d.burst ?? 1) - 1;
    this.burstTimer = d.burstGap ?? 0.2;
    this.fireFan(bullets, ox, oy, tx, ty);
  }

  private fireFan(bullets: BulletSystem, ox: number, oy: number, tx: number, ty: number): void {
    const d = this.def;
    if (d.type !== 'aimed') return;
    const base = Math.atan2(ty - oy, tx - ox);
    const half = (d.spreadDeg / 2) * DEG;
    for (let i = 0; i < d.count; i++) {
      const t = d.count === 1 ? 0.5 : i / (d.count - 1);
      const a = base - half + t * half * 2;
      this.fire(bullets, ox, oy, Math.cos(a), Math.sin(a), i);
    }
  }

  /** @param index bullet within the volley — odd ones mirror an alternating curl */
  private fire(
    bullets: BulletSystem, x: number, y: number, dirX: number, dirY: number, index: number,
  ): void {
    const d = this.def;
    bullets.spawn({
      x,
      y,
      dirX,
      dirY,
      speed: d.speed,
      life: d.life ?? DEFAULT_LIFE,
      radius: d.radius,
      damage: d.damage,
      friendly: false,
      texKey: d.bullet,
      rotates: d.rotates ?? DIRECTIONAL.has(d.bullet),
      motion: d.motion,
      curveSign: d.motion?.curveAlternate && index % 2 === 1 ? -1 : 1,
    });
  }
}
